/**
 * Constants and pure helpers shared by the four plan/product hooks. React-free
 * (AGENTS.md §4) so both chromes - web_admin's Tailwind cards and web_crm's
 * "crm-" vocabulary - can lean on exactly the same values.
 */
import type { LocaleMap, ProductDTO } from "../types";

/** The one product that carries entitlements/subscriptions. */
export const PLATFORM_PRODUCT_CODE = "platform";

/**
 * What a service's packages do: gate platform features, drive site monitoring
 * (the Veebihaldus service, docs/saidiseire-paketid.md), or nothing (a price list).
 */
export type ServiceKind = "platform" | "monitoring" | "display";

/** The kind of a service; without the product row, the platform code alone decides. */
export function serviceKind(product: Pick<ProductDTO, "isPlatform" | "monitoring"> | undefined, productCode?: string): ServiceKind {
  if (product) return product.isPlatform ? "platform" : product.monitoring ? "monitoring" : "display";
  return productCode === PLATFORM_PRODUCT_CODE ? "platform" : "display";
}

/** The i18n key of a service's badge. */
export function serviceBadgeKey(kind: ServiceKind): string {
  return kind === "platform" ? "products.platformBadge" : kind === "monitoring" ? "products.monitoringBadge" : "products.displayBadge";
}

/** Content locales a package/service name and description can be edited in (aishop pattern). */
export const EDIT_LOCALES = ["et", "en"] as const;

/** Machine-readable error code of a rejected `ApiClient` promise (`{ body: { error } }`). */
export function errCodeStr(e: unknown): string | undefined {
  return (e as { body?: { error?: string } })?.body?.error;
}

/** Drop empty values from an i18n map (so blank "en" entries are never saved). */
export function cleanMap(m: LocaleMap): LocaleMap {
  return Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));
}
